import { useMedicalInfoWorkflow } from "../hooks/useMedicalInfoFlow"
import { Steps as JourneySteps } from "../steps/journey/step-library"
import { journeySteps, journeyWorkflows, Workflows as JourneyWorkflows } from "../steps/journey/workflow"
import {
  medicalQuestionsSteps,
  medicalQuestionsWorkflows,
  Steps as MedicalQuestionsSteps
} from "../steps/medical-questions/workflows"
import { StateMachineProvider, StateMachineStepView } from "../../context/stateMachine/StateMachineV2"
import { RoutingFlowContainer } from "./RoutingFlowContainer"
import React, { useEffect } from "react"
import { useBodyClass } from "../../../utils/Hooks"
import { useHistory } from "react-router-dom"
import useCarrotPlanEmployeeSettings from "../hooks/useCarrotPlanEmployeeSettings"
import { CaseRateRenewalsStatus, useGetCaseRateRenewalStatus } from "../case-rate-renewals/useQueryCaseRateRenewals"
import { Progress } from "@carrotfertility/carotene-core"
import {
  CarrotPlanRequestType,
  useGetInProgressCarrotPlanRequestType
} from "#/components/carrot-plans/hooks/useGetInProgressCarrotPlanRequestType"
import { CarrotPlansPaths, QueryStrings } from "#/utils/Paths"
import { useGetCarrotPlanRequestStatus } from "#/components/carrot-plans/hooks/useCarrotPlanRequestStatus"

export enum Sections {
  JOURNEY = "Journey",
  MEDICAL = "Medical"
}

export default function UpdateRoutingFlow(): JSX.Element {
  useBodyClass("bg-warm-gray")
  const history = useHistory()
  const { workflow: desiredMedicalWorkflow } = useMedicalInfoWorkflow()
  const { data, showAdvancedCheckInFeatures } = useGetCaseRateRenewalStatus()
  const {
    isDoneForNow,
    appIsUnlockedAndPendingCarrotPlan,
    isLoading: isLoadingEmployeeSettings
  } = useCarrotPlanEmployeeSettings()
  const { data: inProgressCarrotPlanRequestType, isLoading: isInProgressRequestTypeLoading } =
    useGetInProgressCarrotPlanRequestType()
  const { data: cpRequestStatusQuery, isLoading: isCpRequestStatusLoading } = useGetCarrotPlanRequestStatus()

  const isJourneyChangeFlow = window.location.href.includes(CarrotPlansPaths.JOURNEY_CHANGE)
  const needsToStartJourneyChangeFlow =
    isJourneyChangeFlow &&
    inProgressCarrotPlanRequestType !== CarrotPlanRequestType.JOURNEY_CHANGE_INTAKE &&
    !cpRequestStatusQuery?.carrotPlanIsOnTheWay

  const isLoading = isLoadingEmployeeSettings || isInProgressRequestTypeLoading || isCpRequestStatusLoading

  useEffect(() => {
    if (
      data !== CaseRateRenewalsStatus.GATED &&
      !isJourneyChangeFlow &&
      !showAdvancedCheckInFeatures &&
      !isDoneForNow &&
      !appIsUnlockedAndPendingCarrotPlan &&
      !isLoading
    ) {
      history.push("/")
    } else if (needsToStartJourneyChangeFlow && !isLoading) {
      history.push(`/?dialog=${QueryStrings.JOURNEY_CHANGE_CTA}`)
    }
  }, [
    data,
    isLoading,
    isDoneForNow,
    isJourneyChangeFlow,
    showAdvancedCheckInFeatures,
    appIsUnlockedAndPendingCarrotPlan,
    inProgressCarrotPlanRequestType,
    history,
    needsToStartJourneyChangeFlow
  ])

  const journeySection = {
    name: Sections.JOURNEY,
    initialStep: JourneySteps.WHAT_KIND_OF_JOURNEY,
    desiredWorkflow: JourneyWorkflows.JOURNEY,
    steps: journeySteps,
    workflows: journeyWorkflows
  }

  const medicalQuestionsSection = {
    name: Sections.MEDICAL,
    initialStep: MedicalQuestionsSteps.WELCOME,
    desiredWorkflow: desiredMedicalWorkflow,
    steps: medicalQuestionsSteps,
    workflows: medicalQuestionsWorkflows
  }

  return isLoading ? (
    <Progress />
  ) : (
    <StateMachineProvider
      initialSection={Sections.JOURNEY}
      stateMachineDescription={[journeySection, medicalQuestionsSection]}
      onComplete={null}
    >
      <RoutingFlowContainer>
        <StateMachineStepView />
      </RoutingFlowContainer>
    </StateMachineProvider>
  )
}
